import React, { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import ImageGridItem from './ImageGridItem'
import { useDispatch, useSelector } from 'react-redux';
import { fetchGalleryImage } from '../../redux/action';
import { IMAGE_BASE_URL } from '../../Utills/config';
import { SectionLoader } from '../../component/common/Loader';
import EmptyScreen from '../../component/common/EmptyScreen';

const ImageSelectionModal = ({ onClose, onSelectImage }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null); // Stores the URL of the selected image
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const placeholderImages = useSelector((state) => state?.admin?.get || []);


  // Placeholder images (replace with API call for real data)
  // const placeholderImages = [
  //   { id: '1', url: 'https://via.placeholder.com/150/FF5733/FFFFFF?text=Image+1' },
  //   { id: '2', url: 'https://via.placeholder.com/150/33FF57/FFFFFF?text=Image+2' },
  //   { id: '3', url: 'https://via.placeholder.com/150/3357FF/FFFFFF?text=Image+3' },
  //   { id: '4', url: 'https://via.placeholder.com/150/FFFF33/000000?text=Image+4' },
  //   { id: '5', url: 'https://via.placeholder.com/150/FF33FF/FFFFFF?text=Image+5' },
  //   { id: '6', url: 'https://via.placeholder.com/150/33FFFF/000000?text=Image+6' },
  //   { id: '7', url: 'https://via.placeholder.com/150/8A2BE2/FFFFFF?text=Image+7' },
  //   { id: '8', url: 'https://via.placeholder.com/150/FFD700/000000?text=Image+8' },
  //   { id: '9', url: 'https://via.placeholder.com/150/4682B4/FFFFFF?text=Image+9' },
  // ];

  // In a real application, you'd fetch images from an API (e.g., Unsplash)
  const fetchImages = async (query = '') => {
    setLoading(true);
    setError(null);
    try {
      const filteredImages = placeholderImages.filter(img =>
        img.url.toLowerCase().includes(query.toLowerCase()) ||
        img.id.toLowerCase().includes(query.toLowerCase()) ||
        (img.url.toLowerCase().includes(query.toLowerCase()) && !query) // If query is empty, show all
      );
      setImages(filteredImages);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

 useEffect(() => {
  dispatch(fetchGalleryImage(searchTerm));
}, [searchTerm]);


  const handleSearch = () => {
    fetchImages(searchTerm);
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleSelectButtonClick = () => {
    if (selectedImage) {
      onSelectImage(selectedImage); // Pass the selected image URL back to parent
    } else {
      toast.info('Please select an image first!');
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={modalHeaderStyle}>
          <h3>Select an Image</h3>
          <button type="button" onClick={onClose} style={closeButtonStyle}>&times;</button>
        </div>

        <div style={searchContainerStyle}>
          <input
            type="text"
            placeholder="Search images..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <button type="button" onClick={handleSearch} style={searchButtonStyle}>Search</button>
        </div>

        {loading && <SectionLoader label="Loading images..." rows={2} compact />}
        {error && <p style={{ color: "var(--ui-danger-text)" }}>Error: {error}</p>}

        <div style={imageGridContainerStyle}>
          {placeholderImages.length > 0 ? (
            placeholderImages.map((image) => (
              <ImageGridItem
                key={image.menuImageId}
                imageUrl={IMAGE_BASE_URL +image.imagePath}
                isSelected={selectedImage === IMAGE_BASE_URL + image.imagePath}
                onClick={handleImageClick}
              />
            ))
          ) : (
            !loading && <EmptyScreen compact message="No images found." subText="Try another search or upload an image from your device." />
          )}
        </div>

        <div style={modalFooterStyle}>
          <button type="button" onClick={handleSelectButtonClick} disabled={!selectedImage} style={selectImageButtonStyle}>
            Select Image
          </button>
          <button type="button" onClick={onClose} style={cancelButtonStyle}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ... (Styling for Modal and its elements) ...
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: "var(--ui-surface)",
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  width: '90%',
  maxWidth: '700px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '15px',
  borderBottom: "1px solid var(--ui-surface-muted)",
  paddingBottom: '10px',
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
  color: "var(--ui-border)",
};

const searchContainerStyle = {
  display: 'flex',
  marginBottom: '15px',
};

const searchInputStyle = {
  flexGrow: 1,
  padding: '10px',
  border: "1px solid var(--ui-border)",
  borderRadius: '4px',
  marginRight: '10px',
};

const searchButtonStyle = {
  backgroundColor: "var(--ui-primary)",
  color: "var(--ui-on-primary)",
  padding: '10px 15px',
  borderRadius: '4px',
  border: 'none',
  cursor: 'pointer',
};

const imageGridContainerStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
  gap: '10px',
  padding: '10px 0',
  overflowY: 'auto', // Make image grid scrollable
  flexGrow: 1, // Allow grid to take available space
};

const modalFooterStyle = {
  marginTop: '20px',
  borderTop: "1px solid var(--ui-surface-muted)",
  paddingTop: '15px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
};

const selectImageButtonStyle = {
  backgroundColor: "var(--ui-success)",
  color: "var(--ui-on-primary)",
  padding: '10px 15px',
  borderRadius: '4px',
  border: 'none',
  cursor: 'pointer',
};

const cancelButtonStyle = {
  backgroundColor: '#dc3545',
  color: "var(--ui-on-primary)",
  padding: '10px 15px',
  borderRadius: '4px',
  border: 'none',
  cursor: 'pointer',
};

export default ImageSelectionModal;
